import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  List,
  ListItem,
  useColorModeValue,
  useOutsideClick,
} from '@chakra-ui/react';
import { Field } from 'formik';
import type React from 'react';
import { useState, useRef } from 'react';

type Option = {
  value: string;
  label: string;
};

export interface FreeFormSelectFieldProps {
  id?: string;
  label?: string | any;
  extra?: React.ReactNode;
  placeholder?: string;
  name: string;
  options: Option[];
  errors?: string;
  touched?: boolean;
  isRequired?: boolean;
  full?: boolean;
  rightElement?: React.ReactNode;
  fontSize?: any;
  fontWeight?: string;
  extraInfo?: React.ReactNode;
  italic?: boolean;
  partialLeft?: boolean;
  partialRight?: boolean;
  isDisabled?: boolean;
  placeholderFontSize?: string;
  placeholderFontWeight?: string;
  placeholderFontColor?: string;
  labelFontSize?: string;
  [x: string]: any;
}

export function FreeFormSelectField(props: FreeFormSelectFieldProps) {
  const {
    id,
    label,
    placeholder,
    mb,
    isRequired,
    name,
    options,
    helpers,
    value,
    errors,
    touched,
    minW,
    maxW,
    fontSize,
    fontWeight,
    extraInfo,
    italic,
    field,
    meta,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    isDisabled,
    placeholderFontSize,
    placeholderFontWeight,
    placeholderFontColor,
    labelFontSize,
    onDark,
    ...rest
  } = props;

  const { setValue } = helpers;
  const isError = !!(errors && errors.length > 0);
  const [inputValue, setInputValue] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  useOutsideClick({
    ref: ref,
    handler: () => setIsOpen(false),
  });

  const handleChange = (event: { target: { value: any } }) => {
    const value = event.target.value;
    setInputValue(value);
    setFilteredOptions(
      options.filter((option) => option.label.toLowerCase().includes(value.toLowerCase())),
    );
    setIsOpen(true);
    setFocusedOptionIndex(0);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' && focusedOptionIndex < filteredOptions.length - 1) {
      setFocusedOptionIndex(focusedOptionIndex + 1);
    } else if (event.key === 'ArrowUp' && focusedOptionIndex > 0) {
      setFocusedOptionIndex(focusedOptionIndex - 1);
    } else if (event.key === 'Enter' && isOpen) {
      setInputValue(filteredOptions[focusedOptionIndex].value);
      setValue(filteredOptions[focusedOptionIndex].value);
      setIsOpen(false);
    }
  };

  const handleSelect = (option: { value: any; label?: string }) => {
    setInputValue(option.value);
    setValue(option.value);
    setIsOpen(false);
  };
  return (
    <Flex direction="column" mb={mb ?? '30px'} width="full" ref={ref} pos="relative">
      <FormControl isRequired={isRequired} isInvalid={isError} isDisabled={isDisabled}>
        {label && (
          <FormLabel
            display="flex"
            htmlFor={id}
            fontSize={labelFontSize || '2xl'}
            lineHeight="normal"
            color={textColorPrimary}
            fontWeight="bold"
            mb={4}
            _hover={{ cursor: 'pointer' }}
          >
            {label}
          </FormLabel>
        )}
        <Field
          as={Input}
          value={inputValue}
          name={name}
          autoComplete="off"
          bg="blue.800"
          border="3px solid white"
          borderRadius="10px"
          fontWeight={fontWeight ?? '500'}
          color={textColorPrimary}
          fontSize={fontSize ?? '20px'}
          outline={isError && touched && '1px solid red'}
          placeholder={placeholder}
          h="60px"
          maxH="60px"
          style={{ fontStyle: italic ? 'italic' : 'normal', color: 'white' }}
          css={{ paddingLeft: '28px' }}
          _placeholder={{
            fontWeight: placeholderFontWeight ? placeholderFontWeight : '600',
            color: placeholderFontColor ? placeholderFontColor : 'gray.600',
            fontSize: placeholderFontSize ? placeholderFontSize : '20px',
          }}
          {...rest}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onClick={() => setIsOpen(true)}
        />
        {isOpen && (
          <Box w="100%" bg="white" color="black" borderRadius={10} pos="absolute" zIndex={99999}>
            <List maxH="460px" overflowY="auto">
              {filteredOptions.map((option, index) => (
                <ListItem
                  px={3}
                  py={2}
                  key={index}
                  bg={index === focusedOptionIndex ? 'gray.100' : 'initial'}
                  onClick={() => handleSelect(option)}
                  _hover={{
                    bg: 'gray.100',
                  }}
                  borderRadius={10}
                  cursor="pointer"
                >
                  {option.label}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {isError && touched && <FormErrorMessage ml="10px">{errors}</FormErrorMessage>}
      </FormControl>
    </Flex>
  );
}
